import type { EbayAspect } from "./types";

// item specifics by aspect name
export type AspectValues = Record<string, string[]>;

// the aspect a listing's condition is chosen with, it comes first in the
// category's aspects and is listed as eBay's condition, not an item specific
export const CONDITION_ASPECT = "Condition";

/**
 * The values of an aspect that apply with the other aspects' values, e.g. a
 * ring's 9k metal purity only applies to gold. Values without constraints
 * always apply.
 */
export function applicableValues(aspect: EbayAspect, values: AspectValues) {
  return aspect.values.filter((value) =>
    (value.constraints ?? []).every((constraint) =>
      (values[constraint.aspect] ?? []).some((chosen) =>
        constraint.values.includes(chosen),
      ),
    ),
  );
}

/**
 * Drops the values of selection only aspects that no longer apply, after the
 * aspect they depend on changed. It repeats as a dropped value can be what
 * another one depends on.
 */
export function withoutInapplicable(
  aspects: EbayAspect[],
  values: AspectValues,
): AspectValues {
  let current = values;
  for (;;) {
    let changed = false;
    const next: AspectValues = { ...current };
    for (const aspect of aspects) {
      const chosen = current[aspect.name];
      if (!aspect.selection_only || !chosen?.length) continue;
      const allowed = new Set(
        applicableValues(aspect, current).map((value) => value.value),
      );
      const kept = chosen.filter((value) => allowed.has(value));
      if (kept.length !== chosen.length) {
        next[aspect.name] = kept;
        changed = true;
      }
    }
    if (!changed) return current;
    current = next;
  }
}

/**
 * Item specifics as they're saved: values are trimmed, and empty ones and
 * aspects without any are left out.
 */
export function trimAspectValues(values: AspectValues): AspectValues {
  const trimmed: AspectValues = {};
  for (const [name, chosen] of Object.entries(values)) {
    const kept = [
      ...new Set(chosen.map((value) => value.trim()).filter(Boolean)),
    ];
    if (kept.length > 0) trimmed[name] = kept;
  }
  return trimmed;
}

/**
 * The item specifics of a category to save: values are trimmed, and empty
 * ones and those of aspects the category doesn't have are left out.
 */
export function cleanAspectValues(
  aspects: EbayAspect[],
  values: AspectValues,
): AspectValues {
  const applicable = withoutInapplicable(aspects, values);
  return trimAspectValues(
    Object.fromEntries(
      aspects.map((aspect) => [aspect.name, applicable[aspect.name] ?? []]),
    ),
  );
}
